import React, { useState, useMemo } from 'react';
import {
    Modal,
    Button,
    Text,
    Paper,
    Badge,
    Loader,
    Alert,
    ActionIcon,
    Tooltip,
    Group,
    Title,
    Box,
    Grid,
    Tabs,
    Select,
    Table,
    Textarea,
    TextInput,
    Accordion,
    Divider,
    Anchor,
    Stack
} from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconCopy,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconChevronDown,
    IconChevronUp,
    IconEdit,
    IconTrash,
    IconCopy as IconClone,
    IconX,
    IconExternalLink,
    IconAlertTriangle,
    IconHistory,
    IconArrowBackUp,
    IconBinaryTree,
    IconInfoCircle,
    IconEye,
    IconEyeOff
} from '@tabler/icons-react';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { useModalFullscreen } from '../../hooks/useModalFullscreen';
import { LoadingState } from '../common/LoadingState';
import { ScimReferenceDisplay } from '../common/ScimReferenceDisplay';
import { HtmlPreview } from '../common/HtmlPreview';
import { ScimSchemaService } from '../../services/ScimSchemaService';
import { ReferencesGraph } from '../common/ReferencesGraph';
import { getEntityId } from '../../utils/userFieldUtils';
import { isAbortError } from '../../utils/abortUtils';
// ScimPhotoService is now used internally by useEntityDetailLoader hook
import { EntityCategorizationService } from '../../services/EntityCategorizationService';
import { MembershipListViewer } from '../common/MembershipListViewer';
import { ReferenceListViewer } from '../common/ReferenceListViewer';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { FilterPreviewDialog } from './FilterPreviewDialog';
import { getEntityValidationWarnings } from '../../validation';
import { TargetTypeLink } from '../common/TargetTypeLink';
import { TargetIdLink } from '../common/TargetIdLink';
import { PolicyRuleIdLink } from '../common/PolicyRuleIdLink';
import AceEditor from 'react-ace';
import 'ace-builds';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
// CentralizedSchemaService is now used internally by useEntityDetailLoader hook
// useTheme replaced with useMantineColorScheme (imported above)
import { WorkflowStepsViewer } from '../workflows/WorkflowStepsViewer';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import { GuidDisplay, isGuid } from '../common/GuidDisplay';
// extractReferenceId and extractReferenceDisplayName are now used internally by useEntityDetailLoader hook
import { SidDisplay } from '../common/SidDisplay';
import { ShadcnDeleteConfirmDialog } from './ShadcnDeleteConfirmDialog';
import { DialogErrorAlert } from './DialogErrorAlert';
import { SqlDisplayComponent } from '../common/SqlDisplayComponent';
import { EntityDetailSummary, EntityDetailSchemaTab, EntityDetailBindingsTab } from './entityDetail';
import { EntityDetailPropertiesRenderer } from './entityDetail/EntityDetailPropertiesRenderer';
import { EntityDetailActionBar } from './entityDetail/EntityDetailActionBar';
import { EntityDetailPhoto } from './entityDetail/EntityDetailPhoto';
import { EntityDetailApprovalModal } from './entityDetail/EntityDetailApprovalModal';
import { ShadcnContentBodyFullScreen } from './entityDetail/ShadcnContentBodyFullScreen';
import {
    entityFieldSectionHasMatch,
    getEntityFieldFilterColProps,
    countEntityFieldFilterMatches,
    type EntityFieldFilterEntry,
} from '../../utils/entityFieldFilterUtils';
import { EntityFieldFilterCountReporter } from '../common/EntityFieldFilterCountReporter';
import { EntityDetailJournalTab } from './entityDetail/EntityDetailJournalTab';
import { EntityDetailWorkflowStepsTab } from './entityDetail/EntityDetailWorkflowStepsTab';
import { EntityDetailRawViewsTab } from './entityDetail/EntityDetailRawViewsTab';
import {
    categorizeEntityProperties,
    buildEntityForSummaryDisplay,
    buildCategorizedPropsFromTier3ReadableAttrs,
    CategorizedProperties,
    hasLocationData as entityHasLocation,
    isReferenceObject,
    shouldRenderAsGuid,
    shouldRenderAsSid,
    isIsoDateTime,
    shouldRenderAsDateTime,
    NON_INDEXED_PROPERTY_STUB,
    isARef
} from '../../utils/entityPropertyUtils';
import { cloneJsonValue, unwindWorkflowConfig, formatWorkflowConfigForDisplay, isJsonFieldReturnedAsString, JSON_FIELD_STRING_WARNING_MESSAGE } from '../../utils/workflowConfigUtils';
import {
    getEntityTypeFlags,
    isSet,
    isEmailTemplate,
    isWorkflow,
    isWorkflowActivity,
    isSetTransition,
    isContent,
    isFile
} from '../../utils/entityTypeDetection';
import { isPluginFunctionEntityType } from '../../utils/pluginFunctionUtils';
import { getEntityConfig } from '../../registry';
import { extractReferenceId, extractReferenceDisplayName } from '../../utils/referenceUtils';
import { useEntityDetailLoader } from '../../hooks/useEntityDetailLoader';
import { useEntityEntitlements } from '../../hooks/useEntityEntitlements';
import { ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD, EAGER_LOAD_NON_INDEXED_MAX_LENGTH, formatTier3AttributeSizeEstimate, getReadableAttributeIdsFromTargetResponse, getReadableAttributesWithTypesFromTargetResponse, getReadableButNoLengthCountAttributeIds, getNonIndexedOrNoLengthCountAttributeRefsFromTier2, hasAnyWritableAttribute, isAdministrator, canDeleteFromTargetResponse, canCreateFromTargetResponse, canApproveRequestFromTargetResponse, getBindingConstraintsFromTargetEntitlements, getBindingConstraintsSummary, getBindingViolationMessage, getTargetEntitlementsList, getTargetEntitlementItemForAttribute, getTier2AttributeBindingForAttribute, tier3AttributeEncrypted, tier3ItemDecryptAllowed, formatTargetEntitlementItemForDisplay, getReadableEntityIds, type EntityEntitlementForTargetResponse, type BindingConstraints } from '../../types/EntityEntitlements';
import { isA, createEntityRef, EntityRef } from '../../types';
import EntitlementsService from '../../services/EntitlementsService';
import { formatDateTime } from '../../utils/dateTimeUtils';
import { getEntityEmoji } from '../../utils/navigationIconUtils';
import { copyToClipboard } from '../../utils/clipboardUtils';
import {
    CONTENT_BODY_FULLSCREEN,
    CONTENT_BODY_HASH_PARAM,
    CONTENT_BODY_LOADED,
    clearContentBodyHashParam,
    getHashQueryParam,
    getHashQueryParams,
    setHashQueryParam,
} from '../../utils/hashQueryParams';
import { detectFieldType, FieldTypeInfo } from '../../utils/fieldTypeDetection';
import { debugService } from '../../services/DebugService';
import { stringifyDebugNotes, debugLog, debugVerboseLog } from '../../utils/debugLog';
import { getAxiosInstance } from '../../services/axiosInstance';
import { getScimConfig } from '../../utils/envUtils';
import { useEntityMapTab, type UseEntityMapTabConfig } from '../../hooks/useEntityMapTab';
import { PaginatedEntityViewer } from '../common/PaginatedEntityViewer';
import {
    base64ToBytes,
    bytesToBlobPart,
    getContentBodyValueAndKey,
    getBestFileContentType,
    getBestFileFilename,
    getWhitespaceIssues,
    hasWhitespaceIssues,
    inferMimeTypeFromFilename,
    isExpectedMultilineField,
} from './entityDetail/entityDetailValueUtils';

const NO_MAP_CONFIG: UseEntityMapTabConfig = {
    mode: 'locality',
    supportsMapping: () => false,
    hasLocationData: () => false
};

export interface EntityDetailDialogProps {
    open: boolean;
    entity: any | null;
    entityType: string;
    onClose: () => void;
    onEdit?: (entity: any, targetEntitlement?: EntityEntitlementForTargetResponse | null) => void;
    onOpenNewUi?: (entity: any, targetEntitlement?: EntityEntitlementForTargetResponse | null) => void;
    onDelete?: (entity: any) => Promise<void> | void;
    onClone?: (entity: any) => void;
    additionalTabs?: Array<{
        label: string;
        value: number;
        content: React.ReactNode;
    }>;
    onTabChange?: (tabIndex: number) => void;
    /** When set, map tab(s) are computed inside this dialog from the loaded entity (fullEntity). Use for dynamic entities so Maps tab appears after data loads. */
    mapTabConfig?: UseEntityMapTabConfig | null;
    selectedEntityId?: string | null; // ID from URL - takes precedence over entity.Id
}

export const EntityDetailDialog: React.FC<EntityDetailDialogProps> = ({
    open,
    entity,
    entityType,
    onClose,
    onEdit,
    onOpenNewUi,
    onDelete,
    onClone,
    additionalTabs = [],
    onTabChange,
    mapTabConfig,
    selectedEntityId
}) => {
    const { colorScheme } = useMantineColorScheme();
    const modalFs = useModalFullscreen(MODAL_SIZES.detail);
    const { isStandard } = useViewModeSafe();
    const { entityTypes } = useEntityTypes();
    const { entityEntitlements } = useEntityEntitlements();
    const isAdmin = isAdministrator(entityEntitlements ?? null);
    const readableEntityIdsLower = React.useMemo(() => {
        const ids = getReadableEntityIds(entityEntitlements ?? null);
        return new Set([...ids].map((id) => (id ?? '').toLowerCase()));
    }, [entityEntitlements]);

    // Track hash changes so deep-link query params can influence the primary entity fetch.
    const [hashTick, setHashTick] = React.useState(0);
    React.useEffect(() => {
        const onHashChange = () => setHashTick((x) => x + 1);
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Use the entity detail loader hook for core loading logic
    // selectedEntityId from URL takes precedence over entity.Id
    const contentBodyMode = React.useMemo<'loaded' | 'fullScreen' | null>(() => {
        try {
            const v = getHashQueryParam(CONTENT_BODY_HASH_PARAM);
            if (v === CONTENT_BODY_LOADED || v === CONTENT_BODY_FULLSCREEN) return v;
            return null;
        } catch {
            return null;
        }
    }, [hashTick]);

    const copyDeepLinkWithBodyLoaded = React.useCallback(() => {
        try {
            const hash = window.location.hash ?? '';
            const idx = hash.indexOf('?');
            const base = idx >= 0 ? hash.slice(0, idx) : hash;
            const params = getHashQueryParams();
            params.set(CONTENT_BODY_HASH_PARAM, CONTENT_BODY_LOADED);
            const nextHash = params.toString() ? `${base}?${params.toString()}` : base;
            const url = `${window.location.origin}${window.location.pathname}${nextHash}`;
            copyToClipboard(url);
            notifications.show({ color: 'green', message: 'Link copied (Body will be loaded on open).' });
        } catch {
            copyToClipboard(window.location.href);
            notifications.show({ color: 'yellow', message: 'Link copied.' });
        }
    }, []);

    const {
        fullEntity,
        isLoading,
        error,
        photo,
        schema,
        deleteState,
        reference,
        actionWorkflowError,
        actionWorkflowValidating,
        possessiveTargetAttributeInfo,
        targetEntitlement,
        targetEntitlementError,
        loadNonIndexedAttribute,
        loadingAttribute,
        entityService
    } = useEntityDetailLoader({ open, entity, entityType, selectedEntityId, includeContentBody: contentBodyMode != null });

    // RequestApproval decision dialog state (Approve/Deny)
    const [approvalDecisionDialogOpen, setApprovalDecisionDialogOpen] = React.useState(false);
    const [approvalDecision, setApprovalDecision] = React.useState<'Approved' | 'Denied'>('Approved');
    const [fieldFilterQuery, setFieldFilterQuery] = React.useState('');
    const [fieldFilterCounts, setFieldFilterCounts] = React.useState({ matching: 0, total: 0 });

    const handleFieldFilterCountsChange = React.useCallback(
        (counts: { matching: number; total: number }) => {
            setFieldFilterCounts(counts);
        },
        []
    );

    React.useEffect(() => {
        if (!open) {
            setFieldFilterQuery('');
            setFieldFilterCounts({ matching: 0, total: 0 });
        }
    }, [open]);

    // Ensure tier2 per-entity entitlements are fetched for this entity type for all users.
    // Tier2 drives UI actions (create/edit/delete) and view fallbacks even for admins.
    React.useEffect(() => {
        if (!open) return;
        const key = (entityType ?? '').trim();
        if (!key) return;
        const cached = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(key);
        if (cached != null) return;
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(key).catch(() => {});
    }, [open, entityType]);

    // Use loaded entity for map tab so Maps tab appears after full data loads (e.g. DropOffAddress, PickupAddress)
    const displayEntityForMapTab = fullEntity ?? entity;
    const filterScopeForTabs = displayEntityForMapTab?.FilterScope || displayEntityForMapTab?.filterScope;
    const filterScopeStringForTabs = typeof filterScopeForTabs === 'string'
        ? filterScopeForTabs
        : (filterScopeForTabs?.Id || filterScopeForTabs?.id || filterScopeForTabs?.DisplayName || filterScopeForTabs?.displayName || '');
    const isSetEntityForTabs = isSet(entityType);
    const detailConfigForTabs = getEntityConfig(entityType ?? '');
    const isWorkflowEntityForTabs = !!detailConfigForTabs?.detailTabs?.showStepsTab;
    const isSchemaAttributeForTabs = !!detailConfigForTabs?.detailTabs?.showBindingsTab;
    const additionalTabsStartIndexForMap = 3 + (isWorkflowEntityForTabs ? 1 : 0) + (isSchemaAttributeForTabs ? 1 : 0);

    const mapTabResult = useEntityMapTab({
        entity: displayEntityForMapTab,
        entityType,
        config: mapTabConfig ?? NO_MAP_CONFIG,
        additionalTabsStartIndex: additionalTabsStartIndexForMap
    });

    const effectiveAdditionalTabs = mapTabConfig ? mapTabResult.additionalTabs : additionalTabs;

    // Destructure photo state
    const { photoData, photoLoading, shouldShowPhoto, photoExpanded, setPhotoExpanded } = photo;
    // Destructure schema state
    const { schemaAttributes, schemaLoading } = schema;
    // Destructure delete state
    const { isDeleting, deleteError, deleteDialogOpen, setIsDeleting, setDeleteError, setDeleteDialogOpen } = deleteState;
    // Destructure reference state
    const { referenceDialogOpen, referenceEntity, referenceEntityType, referenceLoading, handleReferenceClick, closeReferenceDialog } = reference;

    // Revert (journal entry): send inverse operation(s) to the target entity via PATCH
    const [isReverting, setIsReverting] = useState(false);
    const [revertError, setRevertError] = useState<string | null>(null);

    // PluginFunction $pivot action
    const [isPivotLoading, setIsPivotLoading] = useState(false);
    const [pivotError, setPivotError] = useState<string | null>(null);
    const [pivotResult, setPivotResult] = useState<any>(null);
    const pluginFunctionPivotAbortRef = React.useRef<AbortController | null>(null);

    // Content Body: deep-linkable full-screen viewer
    const [contentBodyFullScreenOpen, setContentBodyFullScreenOpen] = useState(false);
    const contentBodyFullScreenRequestedRef = React.useRef(false);

    const handlePluginFunctionPivot = React.useCallback(async () => {
        const ent = fullEntity || entity;
        const entityId = ent?.Id ?? ent?.id;
        if (!entityId || !entityService) return;
        // Abort any prior in-flight pivot request (e.g. user clicks multiple times or dialog closes)
        pluginFunctionPivotAbortRef.current?.abort();
        const controller = new AbortController();
        pluginFunctionPivotAbortRef.current = controller;
        setIsPivotLoading(true);
        setPivotError(null);
        setPivotResult(null);
        try {
            const data = await entityService.callPluginFunctionPivot(String(entityId), controller.signal);
            setPivotResult(data);
        } catch (err: any) {
            if (isAbortError(err, controller.signal)) return;
            const msg = err?.response?.data?.Message ?? err?.message ?? 'Pivot request failed.';
            setPivotError(msg);
        } finally {
            setIsPivotLoading(false);
        }
    }, [fullEntity, entity, entityService]);

    React.useEffect(() => {
        // Cancel any in-flight pivot request when dialog closes/unmounts.
        if (open) return;
        pluginFunctionPivotAbortRef.current?.abort();
        pluginFunctionPivotAbortRef.current = null;
    }, [open]);

    const openContentBodyFullScreen = React.useCallback(async () => {
        if (!isContent(entityType)) return;
        setHashQueryParam(CONTENT_BODY_HASH_PARAM, CONTENT_BODY_FULLSCREEN);
        setContentBodyFullScreenOpen(true);
    }, [entityType]);

    // Deep link: #...?...&contentBody=fullScreen will eager-load Body and open full-screen
    React.useEffect(() => {
        if (!open) return;
        const isContentType = isContent(entityType);
        if (!isContentType) return;

        const wantsFull = getHashQueryParam(CONTENT_BODY_HASH_PARAM) === CONTENT_BODY_FULLSCREEN;
        if (!wantsFull) {
            contentBodyFullScreenRequestedRef.current = false;
            return;
        }
        if (contentBodyFullScreenRequestedRef.current) return;
        contentBodyFullScreenRequestedRef.current = true;

        void openContentBodyFullScreen();
    }, [open, entityType, openContentBodyFullScreen]);

    const handleRevertJournalEntry = React.useCallback(async () => {
        const entry = fullEntity || entity;
        if (!entry || !entityService) return;
        const targetId = entry.TargetId || entry.targetId;
        const targetType = entry.TargetType || entry.targetType;
        const entryId = entry.Id || entry.id;
        if (!targetId || !targetType || !entryId) {
            setRevertError('Missing TargetId, TargetType, or journal entry Id.');
            return;
        }
        setIsReverting(true);
        setRevertError(null);
        try {
            // Operations is an array; support legacy singular Operation for backward compat
            const operationsArray = entry.Operations ?? entry.operations;
            let opRecord = Array.isArray(operationsArray) && operationsArray.length > 0
                ? operationsArray[0]
                : entry.Operation ?? entry.operation;
            if (!opRecord) {
                const full = await entityService.getEntityById(entryId, 'JournalEntry', false);
                const fullOps = full?.Operations ?? full?.operations;
                opRecord = Array.isArray(fullOps) && fullOps.length > 0 ? fullOps[0] : full?.Operation ?? full?.operation;
            }
            if (!opRecord) {
                setRevertError('Cannot revert: operation data not available for this journal entry.');
                return;
            }
            const op = (opRecord.Operation ?? opRecord.operation ?? '').toString();
            const path = (opRecord.Attribute ?? opRecord.attribute ?? '').toString();
            const value = opRecord.OperationValue ?? opRecord.operationValue ?? opRecord.value;
            const previousValue = opRecord.PreviousValue ?? opRecord.previousValue ?? opRecord.previous;
            if (!path) {
                setRevertError('Cannot revert: attribute path not available.');
                return;
            }
            let inverseOp: { op: string; path: string; value?: unknown };
            if (op === 'Add') {
                inverseOp = { op: 'Remove', path, value };
            } else if (op === 'Remove') {
                inverseOp = { op: 'Add', path, value };
            } else if (op === 'Replace' || op === 'Delete') {
                if (previousValue === undefined) {
                    setRevertError('Cannot revert: previous value not available.');
                    return;
                }
                inverseOp = { op: 'Replace', path, value: previousValue };
            } else {
                setRevertError(`Cannot revert: unsupported operation type "${op}".`);
                return;
            }
            await entityService.patchEntity(targetId, { Operations: [inverseOp] }, targetType);
            onClose();
        } catch (err: any) {
            const msg = err?.response?.data?.Message ?? err?.message ?? 'Revert failed.';
            setRevertError(msg);
        } finally {
            setIsReverting(false);
        }
    }, [fullEntity, entity, entityService, onClose]);

    // Local UI state
    const [activeTab, setActiveTab] = React.useState(0);
    const [filterPreviewOpen, setFilterPreviewOpen] = useState(false);
    const [schemaForSummary, setSchemaForSummary] = useState<any | null>(null);
    const [filterPreviewFilter, setFilterPreviewFilter] = useState<string>('');
    const [filterPreviewScope, setFilterPreviewScope] = useState<string>('');
    const [filterPreviewTitleOverride, setFilterPreviewTitleOverride] = useState<string | undefined>(undefined);
    const [filterPreviewNoteMessage, setFilterPreviewNoteMessage] = useState<string | undefined>(undefined);
    const [filterPreviewAttributes, setFilterPreviewAttributes] = useState<string[] | undefined>(undefined);

    const handleOpenFilterPreview = React.useCallback((config: {
        filter: string;
        scope: string;
        titleOverride?: string;
        noteMessage?: string;
        attributes?: string[];
    }) => {
        setFilterPreviewFilter(config.filter);
        setFilterPreviewScope(config.scope);
        setFilterPreviewTitleOverride(config.titleOverride);
        setFilterPreviewNoteMessage(config.noteMessage);
        setFilterPreviewAttributes(config.attributes);
        setFilterPreviewOpen(true);
    }, []);

    const handleRequestApprovalDecision = React.useCallback((decision: 'Approved' | 'Denied') => {
        setApprovalDecision(decision);
        setApprovalDecisionDialogOpen(true);
    }, []);

    const handleApprovalSuccess = React.useCallback(() => {
        setApprovalDecisionDialogOpen(false);
        setApprovalDecision('Approved');
        notifications.show({
            title: approvalDecision === 'Denied' ? 'Denied' : 'Approved',
            message: 'Decision submitted.',
            color: approvalDecision === 'Denied' ? 'red' : 'green',
            autoClose: 4000,
        });
    }, [approvalDecision]);

    const actionBarProps = {
        entityType,
        entity,
        fullEntity,
        targetEntitlement,
        isDeleting,
        loadingAttribute,
        isPivotLoading,
        loadNonIndexedAttribute,
        setDeleteDialogOpen,
        handlePluginFunctionPivot,
        onEdit,
        onOpenNewUi,
        onDelete,
        onClone,
        onClose,
        onRequestApprovalDecision: handleRequestApprovalDecision,
        onPreviewMembers: handleOpenFilterPreview,
    };

    // Bindings state for schema attributes
    const [bindings, setBindings] = useState<any[]>([]);
    const [bindingsLoading, setBindingsLoading] = useState(false);
    const [bindingsError, setBindingsError] = useState<string | null>(null);
    const [bindingsTabRequested, setBindingsTabRequested] = useState(false);
    /** When true, show tier3 entitlement info icon next to each field label on Summary. */
    const [showFieldDebugInfo, setShowFieldDebugInfo] = useState(false);

    // Load schema for additive Summary display (schema attributes + defaults for omitted values)
    React.useEffect(() => {
        if (!open || !entityType) {
            setSchemaForSummary(null);
            return;
        }
        let cancelled = false;
        EntityCategorizationService.getInstance().fetchSchema(entityType).then((s) => {
            if (!cancelled) setSchemaForSummary(s);
        }).catch(() => {
            if (!cancelled) setSchemaForSummary(null);
        });
        return () => { cancelled = true; };
    }, [open, entityType]);

    const handleTabChange = (newValue: number) => {
        setActiveTab(newValue);
        if (mapTabConfig && mapTabResult.mapTabIndex !== null && newValue === mapTabResult.mapTabIndex) {
            mapTabResult.handleTabChange(newValue);
        }
        if (onTabChange) {
            onTabChange(newValue);
        }

        // Check if bindings tab was clicked for schema attributes
        const isSchemaAttributeEntity = entityType?.toLowerCase() === 'schemaattribute';
        if (isSchemaAttributeEntity && fullEntity) {
            const filterScope = fullEntity?.FilterScope || fullEntity?.filterScope;
            const filterScopeString = typeof filterScope === 'string'
                ? filterScope
                : (filterScope?.Id || filterScope?.id || filterScope?.DisplayName || filterScope?.displayName || '');
            const hasStepsTab = !!getEntityConfig(entityType ?? '')?.detailTabs?.showStepsTab;
            const bindingsTabIndex = 3 + (hasStepsTab ? 1 : 0);

            if (newValue === bindingsTabIndex && !bindingsTabRequested) {
                setBindingsTabRequested(true);
            }
        }
    };

    // Attribute bindings disabled: not compatible with x-on-behalf-of. Bindings tab will show empty.
    React.useEffect(() => {
        if (!bindingsTabRequested || !fullEntity || entityType?.toLowerCase() !== 'schemaattribute') {
            return;
        }
        setBindingsLoading(false);
        setBindings([]);
        setBindingsError(null);
    }, [bindingsTabRequested, fullEntity, entityType]);

    // Reset local tab state when entity changes (hook handles entity state reset)
    React.useEffect(() => {
        if (entity?.Id || entityType) {
            setActiveTab(0);
            // Reset bindings state when entity changes
            setBindings([]);
            setBindingsError(null);
            setBindingsTabRequested(false);
            setBindingsLoading(false);
            setShowFieldDebugInfo(false);
        }
    }, [entity?.Id, entityType]);

    // Use full entity if available, otherwise fall back to partial entity
    const displayEntity = fullEntity || entity;
    // Raw Views must show the detail-fetched entity, not the table row. fullEntity is the merged
    // result from getEntityById; displayEntity.rawData can be the table row's payload when fullEntity
    // was merged from table + API (merge keeps table's rawData). So prefer fullEntity for debug.
    const debugEntity = fullEntity ?? (displayEntity?.rawData || displayEntity);

    if (!displayEntity) {
        return null;
    }

    // getEntityEmoji is now imported from navigationIconUtils (aliased as getEntityIcon for backwards compat)
    const getEntityIcon = getEntityEmoji;

    const renderContent = () => {
        if (isLoading) {
            return (
                <LoadingState
                    variant="centered"
                    size={40}
                    label={`Loading ${getSingularLabel(entityType)} details...`}
                />
            );
        }

        // No permission to view: tier3 says no readable attributes and user is not admin â€” show clear message instead of broken/empty sections
        const hasNoReadAccess = targetEntitlement != null && targetEntitlement.IsAdministrator !== true &&
            getTargetEntitlementsList(targetEntitlement).length === 0;
        if (hasNoReadAccess) {
            return (
                <Box py={3} px={2}>
                    <Alert color="yellow" mb="md">
                        You do not have permission to view this {getSingularLabel(entityType).toLowerCase()}.
                    </Alert>
                    <Button variant="filled" onClick={onClose}>
                        Back to list
                    </Button>
                </Box>
            );
        }

        debugVerboseLog('ðŸ” EntityDetailDialog: Rendering tabs, activeTab:', activeTab, 'additionalTabs:', effectiveAdditionalTabs.length);

        // Registry-driven tab visibility
        const detailConfig = getEntityConfig(entityType ?? '');
        const detailTabs = detailConfig?.detailTabs;
        const isWorkflowEntity = !!detailTabs?.showStepsTab;
        const isSchemaAttribute = !!detailTabs?.showBindingsTab;

        // Calculate tab indices â€” in standard mode, show only Summary + Journal
        const summaryTabIndex = 0;
        let nextIdx = 1;
        const referencesTabIndex = isStandard ? -1 : nextIdx++;
        const schemaTabIndex = isStandard ? -1 : nextIdx++;
        const stepsTabIndex = !isStandard && isWorkflowEntity ? nextIdx++ : -1;
        const bindingsTabIndex = !isStandard && isSchemaAttribute ? nextIdx++ : -1;
        const additionalTabsStartIndex = nextIdx;
        if (!isStandard) nextIdx += effectiveAdditionalTabs.length;
        const showJournalTab = !detailTabs?.hideJournalTab;
        const journalTabIndex = showJournalTab ? nextIdx++ : -1;
        const rawViewsTabIndex = isStandard ? -1 : nextIdx++;

        return (
            <>
                <EntityDetailPhoto
                    shouldShowPhoto={shouldShowPhoto}
                    fullEntity={fullEntity}
                    entity={entity}
                    photoData={photoData}
                    photoLoading={photoLoading}
                    photoExpanded={photoExpanded}
                    setPhotoExpanded={setPhotoExpanded}
                />

                <Tabs value={String(activeTab)} onChange={(v) => v != null && handleTabChange(Number(v))}>
                    <Tabs.List>
                        <Tabs.Tab value={String(summaryTabIndex)}>Summary</Tabs.Tab>
                        {referencesTabIndex >= 0 && (
                            <Tabs.Tab value={String(referencesTabIndex)}>References</Tabs.Tab>
                        )}
                        {schemaTabIndex >= 0 && (
                            <Tabs.Tab value={String(schemaTabIndex)}>Schema</Tabs.Tab>
                        )}
                        {stepsTabIndex >= 0 && (
                            <Tabs.Tab value={String(stepsTabIndex)}>Steps</Tabs.Tab>
                        )}
                        {bindingsTabIndex >= 0 && (
                            <Tabs.Tab value={String(bindingsTabIndex)}>Bindings</Tabs.Tab>
                        )}
                        {!isStandard && effectiveAdditionalTabs.map((tab) => (
                            <Tabs.Tab key={tab.value} value={String(tab.value)}>{tab.label}</Tabs.Tab>
                        ))}
                        {journalTabIndex >= 0 && (
                            <Tabs.Tab value={String(journalTabIndex)} leftSection={<IconHistory size={16} />}>Journal</Tabs.Tab>
                        )}
                        {rawViewsTabIndex >= 0 && (
                            <Tabs.Tab value={String(rawViewsTabIndex)}>Raw Views</Tabs.Tab>
                        )}
                    </Tabs.List>
                </Tabs>

                {activeTab === summaryTabIndex && (
                    <EntityDetailSummary
                        targetEntitlement={targetEntitlement}
                        showFieldDebugInfo={showFieldDebugInfo}
                        onToggleFieldDebug={() => setShowFieldDebugInfo((v) => !v)}
                        fieldFilterQuery={fieldFilterQuery}
                        onFieldFilterQueryChange={setFieldFilterQuery}
                        fieldFilterMatchCount={fieldFilterCounts.matching}
                        fieldFilterTotalCount={fieldFilterCounts.total}
                    >
                        <EntityDetailPropertiesRenderer
                            entity={displayEntity}
                            entityType={entityType}
                            fullEntity={fullEntity}
                            targetEntitlement={targetEntitlement}
                            schemaForSummary={schemaForSummary}
                            schemaAttributes={schemaAttributes}
                            entityService={entityService}
                            possessiveTargetAttributeInfo={possessiveTargetAttributeInfo}
                            actionWorkflowError={actionWorkflowError}
                            actionWorkflowValidating={actionWorkflowValidating}
                            isAdmin={isAdmin}
                            readableEntityIdsLower={readableEntityIdsLower}
                            colorScheme={colorScheme}
                            fieldFilterQuery={fieldFilterQuery}
                            handleFieldFilterCountsChange={handleFieldFilterCountsChange}
                            handleReferenceClick={handleReferenceClick}
                            loadNonIndexedAttribute={loadNonIndexedAttribute}
                            loadingAttribute={loadingAttribute}
                            openContentBodyFullScreen={openContentBodyFullScreen}
                            showFieldDebugInfo={showFieldDebugInfo}
                            contentBodyMode={contentBodyMode}
                            copyDeepLinkWithBodyLoaded={copyDeepLinkWithBodyLoaded}
                            onOpenFilterPreview={handleOpenFilterPreview}
                        />
                    </EntityDetailSummary>
                )}

                {activeTab === referencesTabIndex && (
                    <ReferencesGraph entity={displayEntity} />
                )}

                {isWorkflowEntity && activeTab === stepsTabIndex && (
                    <EntityDetailWorkflowStepsTab workflow={fullEntity ?? null} />
                )}

                {isSchemaAttribute && activeTab === bindingsTabIndex && (
                    <EntityDetailBindingsTab
                        bindings={bindings}
                        bindingsLoading={bindingsLoading}
                        bindingsError={bindingsError}
                    />
                )}

                {activeTab === schemaTabIndex && (
                    <EntityDetailSchemaTab
                        schemaAttributes={schemaAttributes}
                        schemaLoading={schemaLoading}
                        entityType={entityType}
                        entityTypes={entityTypes}
                        authResolved={true}
                    />
                )}

                {showJournalTab && activeTab === journalTabIndex && fullEntity && (
                    <EntityDetailJournalTab
                        entityType={entityType}
                        targetId={String(fullEntity.Id || fullEntity.id)}
                        currentEntity={fullEntity}
                    />
                )}

                {activeTab === rawViewsTabIndex && (
                    <EntityDetailRawViewsTab
                        entity={debugEntity}
                        entityType={entityType}
                        targetEntitlement={targetEntitlement}
                    />
                )}

                {/* Render additional tabs - match by actual index, not tab.value */}
                {effectiveAdditionalTabs.map((tab, index) => {
                    const actualTabIndex = additionalTabsStartIndex + index;
                    return (
                        <React.Fragment key={tab.value || index}>
                            {activeTab === actualTabIndex && tab.content}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    return (
        <>
        <Modal
            opened={open}
            onClose={() => {
                clearContentBodyHashParam();
                onClose();
            }}
            size={modalFs.size}
            fullScreen={modalFs.fullScreen}
            styles={{
                content: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
                body: { flex: 1, overflow: 'auto', padding: 'var(--mantine-spacing-md)' },
                header: { padding: 'var(--mantine-spacing-md)' }
            }}
            title={
                <Tooltip label="EntityDetailDialog" withArrow position="top">
                    <span style={{ cursor: 'help' }}>
                        <Group gap="xs" component="span">
                            {getEntityIcon(entityType)}
                            <Text fw={600} component="span">{getSingularLabel(entityType)} Details</Text>
                            {displayEntity?.DisplayName && (
                                <Text c="dimmed" fw={400} ml="xs" component="span">
                                    - {displayEntity.DisplayName}
                                </Text>
                            )}
                            {isLoading && <Loader size={16} />}
                        </Group>
                    </span>
                </Tooltip>
            }
        >
            {error && (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="md">
                    {error}
                </Alert>
            )}
            {targetEntitlementError && (
                <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="md" title="View permissions warning">
                    Entitlements (view permissions) could not be loaded: {targetEntitlementError}. You may see limited or no fields.
                </Alert>
            )}
            <DialogErrorAlert error={deleteError} onClose={() => setDeleteError(null)} />
            <DialogErrorAlert error={revertError} onClose={() => setRevertError(null)} />
            <DialogErrorAlert error={pivotError} onClose={() => setPivotError(null)} />
            {entityType && (fullEntity || entity) && getEntityValidationWarnings(entityType, fullEntity || entity || {}, 'view').map((w, i) => (
                <Alert key={i} color="yellow" mb="md" title={w.severity === 'warning' ? 'Warning' : undefined} icon={<IconAlertTriangle size={16} />}>
                    <Text size="sm">{w.message}</Text>
                    {w.attributeDisplayNames && w.attributeDisplayNames.length > 0 && (
                        <Text size="xs" c="dimmed" mt="xs">Affected fields: {w.attributeDisplayNames.join(', ')}</Text>
                    )}
                </Alert>
            ))}

            <EntityDetailActionBar position="top" {...actionBarProps} />
            {renderContent()}

            <EntityDetailApprovalModal
                opened={approvalDecisionDialogOpen}
                onClose={() => setApprovalDecisionDialogOpen(false)}
                decision={approvalDecision}
                entityService={entityService}
                fullEntity={fullEntity}
                entity={entity}
                onSuccess={handleApprovalSuccess}
            />

            <EntityDetailActionBar position="bottom" {...actionBarProps} />

            {/* Filter Preview Dialog */}
            <FilterPreviewDialog
                open={filterPreviewOpen}
                onClose={() => {
                    setFilterPreviewOpen(false);
                    setFilterPreviewTitleOverride(undefined);
                    setFilterPreviewNoteMessage(undefined);
                    setFilterPreviewAttributes(undefined);
                }}
                filter={filterPreviewFilter}
                filterScope={filterPreviewScope}
                entityType={filterPreviewScope}
                titleOverride={filterPreviewTitleOverride}
                noteMessage={filterPreviewNoteMessage}
                attributes={filterPreviewAttributes}
            />

            {/* Nested read-only dialog for referenced entities */}
            <EntityDetailDialog
                open={referenceDialogOpen}
                entity={referenceLoading ? null : referenceEntity}
                entityType={referenceEntityType}
                onClose={closeReferenceDialog}
            />

            {/* Delete Confirmation Dialog */}
            {onDelete && entity && (
                <ShadcnDeleteConfirmDialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setDeleteError(null);
                    }}
                    onConfirm={async () => {
                        try {
                            setIsDeleting(true);
                            setDeleteError(null);
                            await onDelete(entity);
                            setDeleteDialogOpen(false);
                        } catch (error: any) {
                            console.error(`Error deleting ${entityType}:`, error);

                            let errorMessage = `Failed to delete ${entityType}`;
                            const statusCode = error?.response?.status || error?.status;
                            if (statusCode === 403) {
                                const responseData = error?.response?.data;
                                if (responseData?.Message) {
                                    errorMessage = responseData.Message;
                                } else if (error?.message) {
                                    errorMessage = error.message;
                                }
                            } else if (error instanceof Error) {
                                errorMessage = error.message;
                            }

                            setDeleteError(errorMessage);
                        } finally {
                            setIsDeleting(false);
                        }
                    }}
                    entityType={entityType}
                    entityName={entity.DisplayName || entity.Name || entity.Id || 'Unknown'}
                    isDeleting={isDeleting}
                    error={deleteError}
                />
            )}

            {/* $pivot result dialog (PluginFunction) */}
            <Modal opened={pivotResult !== null} onClose={() => setPivotResult(null)} size={MODAL_SIZES.detail} title={<Tooltip label="EntityDetailDialog ($pivot)" withArrow position="top"><span style={{ cursor: 'help' }}>$pivot response</span></Tooltip>}>
                <Box mt="xs">
                    <Text size="sm" c="dimmed" mb="xs">
                        Response from entities/pluginfunctions/&#123;id&#125;/$pivot
                    </Text>
                    <Paper withBorder p="md" mah={400} style={{ overflow: 'auto' }}>
                        <Box component="pre" m={0} fz={12} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {pivotResult != null ? JSON.stringify(pivotResult, null, 2) : ''}
                        </Box>
                    </Paper>
                </Box>
            </Modal>
        </Modal>

        <ShadcnContentBodyFullScreen
            opened={contentBodyFullScreenOpen}
            onClose={() => setContentBodyFullScreenOpen(false)}
            fullEntity={fullEntity}
            entity={entity}
            entityType={entityType}
            contentBodyMode={contentBodyMode}
            copyDeepLinkWithBodyLoaded={copyDeepLinkWithBodyLoaded}
        />
        </>
    );
};
